// Minimal two-pass 6502 assembler (documented opcodes) for purpose-written test and demo tunes.
//
// Syntax: `label:`, `MNE operand`, `.byte a,b,…`, `.word a,…`, `* = $1000`, `; comment`.
// Operands: #imm, zp/abs, ,X / ,Y, (ind), (zp,X), (zp),Y. Expressions: $hex, %bin, dec,
// labels, <expr (low byte), >expr (high byte), a+b, a-b.

type Mode = 'imp' | 'imm' | 'zp' | 'zpx' | 'zpy' | 'abs' | 'abx' | 'aby' | 'ind' | 'izx' | 'izy' | 'rel';

// Columns: imm zp zpx zpy abs abx aby ind izx izy
const COLS: Mode[] = ['imm', 'zp', 'zpx', 'zpy', 'abs', 'abx', 'aby', 'ind', 'izx', 'izy'];
const TABLE: Record<string, (number | null)[]> = {};
const RAW = `
ADC 69 65 75 -- 6D 7D 79 -- 61 71
AND 29 25 35 -- 2D 3D 39 -- 21 31
ASL -- 06 16 -- 0E 1E -- -- -- --
BIT -- 24 -- -- 2C -- -- -- -- --
CMP C9 C5 D5 -- CD DD D9 -- C1 D1
CPX E0 E4 -- -- EC -- -- -- -- --
CPY C0 C4 -- -- CC -- -- -- -- --
DEC -- C6 D6 -- CE DE -- -- -- --
EOR 49 45 55 -- 4D 5D 59 -- 41 51
INC -- E6 F6 -- EE FE -- -- -- --
JMP -- -- -- -- 4C -- -- 6C -- --
JSR -- -- -- -- 20 -- -- -- -- --
LDA A9 A5 B5 -- AD BD B9 -- A1 B1
LDX A2 A6 -- B6 AE -- BE -- -- --
LDY A0 A4 B4 -- AC BC -- -- -- --
LSR -- 46 56 -- 4E 5E -- -- -- --
ORA 09 05 15 -- 0D 1D 19 -- 01 11
ROL -- 26 36 -- 2E 3E -- -- -- --
ROR -- 66 76 -- 6E 7E -- -- -- --
SBC E9 E5 F5 -- ED FD F9 -- E1 F1
STA -- 85 95 -- 8D 9D 99 -- 81 91
STX -- 86 -- 96 8E -- -- -- -- --
STY -- 84 94 -- 8C -- -- -- -- --
`;
for (const line of RAW.trim().split('\n')) {
  const [m, ...codes] = line.trim().split(/\s+/);
  TABLE[m] = codes.map((c) => (c === '--' ? null : parseInt(c, 16)));
}
const IMPLIED: Record<string, number> = {
  BRK: 0x00,
  CLC: 0x18,
  CLD: 0xd8,
  CLI: 0x58,
  CLV: 0xb8,
  DEX: 0xca,
  DEY: 0x88,
  INX: 0xe8,
  INY: 0xc8,
  NOP: 0xea,
  PHA: 0x48,
  PHP: 0x08,
  PLA: 0x68,
  PLP: 0x28,
  RTI: 0x40,
  RTS: 0x60,
  SEC: 0x38,
  SED: 0xf8,
  SEI: 0x78,
  TAX: 0xaa,
  TAY: 0xa8,
  TSX: 0xba,
  TXA: 0x8a,
  TXS: 0x9a,
  TYA: 0x98,
};
const ACCUM: Record<string, number> = { ASL: 0x0a, LSR: 0x4a, ROL: 0x2a, ROR: 0x6a };
const BRANCH: Record<string, number> = {
  BPL: 0x10,
  BMI: 0x30,
  BVC: 0x50,
  BVS: 0x70,
  BCC: 0x90,
  BCS: 0xb0,
  BNE: 0xd0,
  BEQ: 0xf0,
};

export class AsmError extends Error {}

export interface AsmResult {
  origin: number;
  bytes: Uint8Array;
  labels: Map<string, number>;
}

export function assemble(source: string, origin = 0x1000): AsmResult {
  const labels = new Map<string, number>();
  const sizes = new Map<number, number>();
  let out: number[] = [];

  const evaluate = (expr: string, pass: number, line: number): number | null => {
    expr = expr.trim();
    if (expr.startsWith('<')) {
      const v = evaluate(expr.slice(1), pass, line);
      return v === null ? null : v & 0xff;
    }
    if (expr.startsWith('>')) {
      const v = evaluate(expr.slice(1), pass, line);
      return v === null ? null : (v >> 8) & 0xff;
    }
    const m = /^(.*[^+-])\s*([+-])\s*(.+)$/.exec(expr);
    if (m) {
      const a = evaluate(m[1], pass, line);
      const b = evaluate(m[3], pass, line);
      if (a === null || b === null) return null;
      return m[2] === '+' ? a + b : a - b;
    }
    if (/^\$[0-9a-f]+$/i.test(expr)) return parseInt(expr.slice(1), 16);
    if (/^%[01]+$/.test(expr)) return parseInt(expr.slice(1), 2);
    if (/^\d+$/.test(expr)) return parseInt(expr, 10);
    if (/^[a-z_][\w]*$/i.test(expr)) {
      const v = labels.get(expr);
      if (v === undefined) {
        if (pass === 2) throw new AsmError(`line ${line}: unknown label "${expr}"`);
        return null;
      }
      return v;
    }
    throw new AsmError(`line ${line}: bad expression "${expr}"`);
  };

  for (let pass = 1; pass <= 2; pass++) {
    let pc = origin;
    out = [];
    const lines = source.split('\n');
    for (let li = 0; li < lines.length; li++) {
      const lineNo = li + 1;
      let line = lines[li].replace(/;.*$/, '').trim();
      if (!line) continue;
      const lm = /^([a-z_]\w*):\s*(.*)$/i.exec(line);
      if (lm) {
        if (pass === 1) labels.set(lm[1], pc);
        line = lm[2].trim();
        if (!line) continue;
      }
      const org = /^\*\s*=\s*(.+)$/.exec(line);
      if (org) {
        const v = evaluate(org[1], 2, lineNo)!;
        while (pc < v) {
          out.push(0);
          pc++;
        }
        continue;
      }
      if (/^\.(byte|word)\b/i.test(line)) {
        const word = /^\.word/i.test(line);
        for (const part of line.replace(/^\.\w+\s*/, '').split(',')) {
          const v = evaluate(part, pass, lineNo) ?? 0;
          if (word) out.push(v & 0xff, (v >> 8) & 0xff);
          else out.push(v & 0xff);
          pc += word ? 2 : 1;
        }
        continue;
      }
      const im = /^([a-z]{3})\s*(.*)$/i.exec(line);
      if (!im) throw new AsmError(`line ${lineNo}: cannot parse "${line}"`);
      const mne = im[1].toUpperCase();
      const opnd = im[2].trim();

      if (BRANCH[mne] !== undefined) {
        const target = evaluate(opnd, pass, lineNo);
        let off = 0;
        if (target !== null) {
          off = target - (pc + 2);
          if (pass === 2 && (off < -128 || off > 127))
            throw new AsmError(`line ${lineNo}: branch out of range`);
        }
        out.push(BRANCH[mne], off & 0xff);
        pc += 2;
        continue;
      }
      if (!opnd || (opnd.toUpperCase() === 'A' && ACCUM[mne] !== undefined)) {
        const code = IMPLIED[mne] ?? ACCUM[mne];
        if (code === undefined) throw new AsmError(`line ${lineNo}: ${mne} needs an operand`);
        out.push(code);
        pc++;
        continue;
      }
      const row = TABLE[mne];
      if (!row) throw new AsmError(`line ${lineNo}: unknown instruction ${mne}`);
      let mode: Mode;
      let expr: string;
      let m2: RegExpExecArray | null;
      if (opnd.startsWith('#')) {
        mode = 'imm';
        expr = opnd.slice(1);
      } else if ((m2 = /^\((.+),\s*x\)$/i.exec(opnd))) {
        mode = 'izx';
        expr = m2[1];
      } else if ((m2 = /^\((.+)\)\s*,\s*y$/i.exec(opnd))) {
        mode = 'izy';
        expr = m2[1];
      } else if ((m2 = /^\((.+)\)$/.exec(opnd))) {
        mode = 'ind';
        expr = m2[1];
      } else if ((m2 = /^(.+),\s*([xy])$/i.exec(opnd))) {
        mode = m2[2].toLowerCase() === 'x' ? 'abx' : 'aby';
        expr = m2[1];
      } else {
        mode = 'abs';
        expr = opnd;
      }
      const value = evaluate(expr, pass, lineNo);
      // Zero-page forms are chosen in pass 1 only (sizes must not change between passes).
      if (mode === 'abs' || mode === 'abx' || mode === 'aby') {
        let useZp: boolean;
        if (pass === 1) {
          const zpMode = mode === 'abs' ? 'zp' : mode === 'abx' ? 'zpx' : 'zpy';
          useZp = value !== null && value < 0x100 && row[COLS.indexOf(zpMode)] !== null;
          sizes.set(lineNo, useZp ? 2 : 3);
        } else useZp = sizes.get(lineNo) === 2;
        if (useZp) mode = mode === 'abs' ? 'zp' : mode === 'abx' ? 'zpx' : 'zpy';
      }
      const code = row[COLS.indexOf(mode)];
      if (code === null || code === undefined)
        throw new AsmError(`line ${lineNo}: ${mne} does not support ${mode}`);
      const v = value ?? 0;
      out.push(code);
      if (mode === 'abs' || mode === 'abx' || mode === 'aby' || mode === 'ind') {
        out.push(v & 0xff, (v >> 8) & 0xff);
        pc += 3;
      } else {
        out.push(v & 0xff);
        pc += 2;
      }
    }
  }
  return { origin, bytes: Uint8Array.from(out), labels };
}
